import { useEffect, useMemo } from 'react'
import { MdOutlineSearch } from 'react-icons/md'

import { DefaultSearch } from '../interfaces/zoneInterface'
import { GoogleMapsStructuredPlace } from '../interfaces/googleMapsInterfaces'
import { getNavigationByAddress } from '../utils/googleMapsUtils'
import { composeClasses } from '@wiggot/utils/src/strings'
import { ESearchLevel } from '../enums'

interface Props {
  index: number
  selectedIndex: number
  option: DefaultSearch
  className?: string
  itemTextClassName?: string
  focusOptionClassNames?: string
  onSelected: (place: GoogleMapsStructuredPlace) => void
}

const DefaultSearchOption = ({
  index,
  option,
  selectedIndex,
  className = '',
  itemTextClassName = '',
  focusOptionClassNames,
  onSelected
}: Props) => {
  const composition = useMemo(() => {
    if (!option) {
      return {
        street: '',
        neighborhood: '',
        concatenatedRest: ''
      }
    }

    const { name } = option

    const [street, neighborhood, ...rest] = name.split(',')
    const concatenatedRest = rest.join(', ')

    return {
      street,
      neighborhood,
      concatenatedRest
    }
  }, [option])

  const onClick = (): void => {
    const { id, name, type, coordinates } = option

    const basePlace: GoogleMapsStructuredPlace = {
      placeId: id.toString(),
      coordinates,
      fullAddress: name,
      streetNumber: '',
      street: '',
      neighborhood: '',
      town: '',
      location: '',
      postalCode: '',
      searchLevel: type,
      navigation: getNavigationByAddress({
        address: name,
        searchLevel: type === 'zone' ? ESearchLevel.NEIGHBORHOOD : type,
        notCountry: type !== 'zone'
      })
    }

    if (type === 'zone') {
      onSelected({
        ...basePlace,
        zoneId: id,
        searchLevel: ESearchLevel.NEIGHBORHOOD
      })

      return
    }

    onSelected(basePlace)
  }

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Enter' && selectedIndex === index) {
        onClick()
      }
    }

    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [selectedIndex])

  return (
    <button
      type="button"
      onClick={onClick}
      className={composeClasses(
        'w-full flex justify-start text-left items-center gap-x-[10px] py-[5px] hover:bg-gray-100 text-text-primary',
        index === selectedIndex && (focusOptionClassNames || 'bg-gray-100'),
        className
      )}
    >
      <MdOutlineSearch className="w-[18px] h-[18px]  !flex justify-center items-center flex-grow-0 flex-shrink-0 basis-auto" />

      <p className={composeClasses('text-xs truncate', itemTextClassName)}>
        {composition.street},{' '}
        <span className="font-bold">{composition.neighborhood}</span>,{' '}
        {composition.concatenatedRest}
      </p>
    </button>
  )
}

export default DefaultSearchOption
